import { SchemaError } from '../errors/SchemaError.js';
import { BigNumber, EnumType, MLString, ObjectType } from '../types.js';
import { PrimitiveParser } from './PrimitiveParser.js';
export class ExternalParser {
    constructor(primitiveValidator, propertyPathValidator) {
        this.primitiveValidator = primitiveValidator;
        this.propertyPathValidator = propertyPathValidator;
        this.primitiveParser = new PrimitiveParser(primitiveValidator);
    }
    parseDefinition(external, type) {
        switch (external.type) {
            case 'string': {
                if (!this.primitiveValidator.validString(external.default)) {
                    throw SchemaError.invalidExternalDefaultType(external, type);
                }
                return external;
            }
            case 'mlstring': {
                if (!this.primitiveValidator.validMlString(external.default)) {
                    throw SchemaError.invalidExternalDefaultType(external, type);
                }
                return Object.assign(Object.assign({}, external), { default: new MLString(external.default) });
            }
            case 'number': {
                if (!this.primitiveValidator.validNumber(external.default)) {
                    throw SchemaError.invalidExternalDefaultType(external, type);
                }
                return external;
            }
            case 'bignumber': {
                if (!this.primitiveValidator.validBigNumber(external.default)) {
                    throw SchemaError.invalidExternalDefaultType(external, type);
                }
                return Object.assign(Object.assign({}, external), { default: new BigNumber(external.default) });
            }
            case 'boolean': {
                if (!this.primitiveValidator.validBoolean(external.default)) {
                    throw SchemaError.invalidExternalDefaultType(external, type);
                }
                return external;
            }
            default: {
                throw SchemaError.invalidExternalType(external, type);
            }
        }
    }
    parseField(doc) {
        this.validateRequiredFields(doc);
        const type = this.primitiveValidator.schema.getType(doc.type);
        if (!type) {
            throw SchemaError.invalidExternalFieldData(doc, `Type is not registered in schema`);
        }
        if (!(type instanceof ObjectType)) {
            throw SchemaError.invalidExternalFieldData(doc, `Invalid type "${type.name}". Object type expected`);
        }
        const typeExternals = type.externals || [];
        const externalSchema = typeExternals.find(external => external.name === doc.name);
        this.validateOrder(doc, type, externalSchema);
        doc.data = this.parseData(doc, type, externalSchema);
    }
    validateRequiredFields(doc) {
        if (!doc.name) {
            throw SchemaError.invalidExternalFieldData(doc, `name is required`);
        }
        if (!doc.type) {
            throw SchemaError.invalidExternalFieldData(doc, `type is required`);
        }
        if (!doc.order) {
            throw SchemaError.invalidExternalFieldData(doc, `order is required`);
        }
        if (!doc.data) {
            throw SchemaError.invalidExternalFieldData(doc, `data is required`);
        }
    }
    validateOrder(doc, type, externalSchema) {
        if (!externalSchema) {
            throw SchemaError.invalidExternalFieldData(doc, `External is not registered in "${type.name}" schema`);
        }
        const missingOrderProperties = [];
        const presentProperties = [];
        externalSchema.properties.forEach(propertyName => {
            if (doc.order.includes(propertyName)) {
                presentProperties.push(propertyName);
            }
            else {
                missingOrderProperties.push(propertyName);
            }
        });
        if (doc.order.includes(externalSchema.name)) {
            presentProperties.push(externalSchema.name);
        }
        else {
            missingOrderProperties.push(externalSchema.name);
        }
        if (missingOrderProperties.length > 0) {
            if (missingOrderProperties.length == 1) {
                throw SchemaError.invalidExternalFieldData(doc, `"${missingOrderProperties}" property is missing in order`);
            }
            else {
                throw SchemaError.invalidExternalFieldData(doc, `"${missingOrderProperties}" properties are missing in order`);
            }
        }
        const redundantOrderFields = [];
        doc.order.forEach(orderField => {
            if (!presentProperties.includes(orderField)) {
                redundantOrderFields.push(orderField);
            }
        });
        if (redundantOrderFields.length > 0) {
            if (redundantOrderFields.length == 1) {
                throw SchemaError.invalidExternalFieldData(doc, `"${redundantOrderFields}" property is redundant in order`);
            }
            else {
                throw SchemaError.invalidExternalFieldData(doc, `"${redundantOrderFields}" properties are redundant in order`);
            }
        }
    }
    parseData(doc, type, externalSchema) {
        const recordTypes = this.buildRecordType(doc, type, externalSchema);
        const invalidValues = [];
        const invalidTypes = [];
        const result = [];
        for (let i = 0; i < doc.data.length; i++) {
            const nextLine = [];
            for (let record = 0; record < recordTypes.length; record++) {
                const typeRef = recordTypes[record];
                const value = doc.data[i][record];
                const parsedValue = this.primitiveParser.parsePrimitive(typeRef, value);
                nextLine.push(parsedValue);
                if (parsedValue === undefined) {
                    invalidValues.push({
                        value
                    });
                    invalidTypes.push(`${doc.order[record]}/${typeRef}`);
                }
            }
            if (invalidValues.length > 0) {
                throw SchemaError.invalidExternalFieldData(doc, `Check "${i + 1}" line "${invalidValues}" values of ${invalidTypes} (name/type)`);
            }
            result.push(nextLine);
        }
        return result;
    }
    buildRecordType(doc, type, externalSchema) {
        return doc.order
            .map(path => {
            if (path !== externalSchema.name) {
                const pathType = this.propertyPathValidator.parsePropertyType(path, type, externalSchema);
                if (pathType instanceof EnumType) {
                    return pathType.valueType.toLowerCase();
                }
                else {
                    return pathType.getTypeReference();
                }
            }
            else {
                return externalSchema.type;
            }
        });
    }
}
//# sourceMappingURL=ExternalParser.js.map